/**
 * Plan Pricing Helper
 *
 * This file centralizes all plan pricing information and Stripe plan IDs
 * to ensure consistency across the frontend and backend.
 */

// Environment detection
export const isProduction = () => {
  const envSet =
    typeof process !== 'undefined'
      ? process.env.NEXT_PUBLIC_VERCEL_ENV || process.env.VERCEL_ENV || ''
      : ''
  return envSet === 'production'
}

// Plan types
export type PlanInterval = 'monthly' | 'yearly'
export type PlanEnvironment = 'test' | 'live'

// Plan identifiers
export enum PlanId {
  MONTHLY_25 = 'monthly-25', // $25/month
  YEARLY_49 = 'yearly-49', // $45/year
  YEARLY_99 = 'yearly-99', // $99/year premium plan
}

// Plan configuration
interface PlanConfig {
  id: PlanId
  name: string
  interval: PlanInterval
  priceInCents: number
  trialDays: number
  stripeIds: {
    test: string
    live: string
  }
}

/**
 * ACTIVE PLANS CONFIGURATION
 *
 * This is the single source of truth for which plans are currently active in the application.
 * Only one plan per interval type can be active at any time.
 *
 * TO CHANGE ACTIVE PLANS:
 * 1. Update the corresponding interval value to the desired PlanId
 * 2. Verify the Stripe price IDs are correct in the PLANS configuration
 * 3. Update UI components if needed
 *
 * TO ADD A NEW PLAN TYPE:
 * 1. Add a new entry to the PlanId enum with appropriate pricing info
 * 2. Add the plan configuration to the PLANS object
 * 3. If it's a new interval type, add it to this ACTIVE_PLANS record
 * 4. If replacing an existing interval, update the value here
 */
export const ACTIVE_PLANS: Record<PlanInterval, PlanId> = {
  monthly: PlanId.MONTHLY_25,
  yearly: PlanId.YEARLY_49,
  // To activate the premium yearly plan, change to:
  // yearly: PlanId.YEARLY_99,
}

// Plan definitions
export const PLANS: Record<PlanId, PlanConfig> = {
  [PlanId.MONTHLY_25]: {
    id: PlanId.MONTHLY_25,
    name: 'Monthly',
    interval: 'monthly',
    priceInCents: 2500, // $25/month
    trialDays: 7,
    stripeIds: {
      test: 'price_1QuwJfF553JNJgnLREsvWaKE', // Test environment
      live: 'price_1Qv5o8F553JNJgnLlgPBp214', // Production environment
    },
  },
  [PlanId.YEARLY_49]: {
    id: PlanId.YEARLY_49,
    name: 'Yearly',
    interval: 'yearly',
    priceInCents: 4900, // $49/year
    trialDays: 30,
    stripeIds: {
      test: 'price_1Qx8e1F553JNJgnLBPCqBoU2', // Test environment
      live: 'price_1Qx8dcF553JNJgnLG0eiRyWt', // Production environment
    },
  },
  [PlanId.YEARLY_99]: {
    id: PlanId.YEARLY_99,
    name: 'Yearly Premium',
    interval: 'yearly',
    priceInCents: 9900, // $99/year
    trialDays: 30,
    stripeIds: {
      test: 'price_1QudBaF553JNJgnLogNPTksc', // Test environment
      live: 'price_1Qv5o8F553JNJgnLAciSFu9M', // Production environment
    },
    // This plan is not currently active - to activate it, update ACTIVE_PLANS.yearly
  },
}

// Helper functions

/**
 * Check if a plan is the active plan for its interval
 */
export const isPlanActive = (planId: PlanId): boolean => {
  const plan = PLANS[planId]
  return ACTIVE_PLANS[plan.interval] === planId
}

/**
 * Get active plans only (one per interval type)
 */
export const getActivePlans = (): PlanConfig[] => {
  return Object.values(ACTIVE_PLANS).map((planId) => PLANS[planId])
}

/**
 * Get the Stripe price ID for a plan
 */
export const getStripePriceId = (planId: PlanId): string => {
  const env = isProduction() ? 'live' : 'test'
  return PLANS[planId].stripeIds[env]
}

/**
 * Get the price in cents for a plan
 */
export const getPriceInCents = (planId: PlanId): number => {
  return PLANS[planId].priceInCents
}

/**
 * Get the price in dollars for a plan
 */
export const getPriceInDollars = (planId: PlanId): number => {
  return PLANS[planId].priceInCents / 100
}

/**
 * Get the monthly price in dollars (even for yearly plans)
 */
export const getMonthlyPriceInDollars = (planId: PlanId): number => {
  const plan = PLANS[planId]
  if (plan.interval === 'monthly') {
    return plan.priceInCents / 100
  } else {
    return plan.priceInCents / 12 / 100
  }
}

/**
 * Get the trial period in days for a plan
 */
export const getTrialDays = (planId: PlanId): number => {
  return PLANS[planId].trialDays
}

/**
 * Calculate the savings percentage when choosing yearly vs monthly
 */
export const calculateYearlySavingsPercentage = (): number => {
  const yearlyPlanId = ACTIVE_PLANS.yearly
  const monthlyPlanId = ACTIVE_PLANS.monthly

  const yearlyPriceInCents = PLANS[yearlyPlanId].priceInCents
  const monthlyPriceInCents = PLANS[monthlyPlanId].priceInCents

  return Math.floor((1 - yearlyPriceInCents / (monthlyPriceInCents * 12)) * 100)
}

/**
 * Get plan ID from interval
 */
export const getPlanIdFromInterval = (interval: PlanInterval): PlanId => {
  return ACTIVE_PLANS[interval]
}
